import { modular } from '@foxitsoftware/foxit-pdf-sdk-for-web-library'
import { captureMessage } from '@sentry/vue'

// Foxit's own typed-signature renderer (CreateInkSignDialogController.textToImg) draws the text
// into a canvas barely taller than the font size, so cursive swashes above/below the em box get
// clipped, and then shrinks the result by devicePixelRatio, which makes it blurry once stamped
// onto the PDF. We replace it with one that renders large and sizes the canvas to the glyphs.

const FONT_SIZE = 160
// Covers anti-aliasing just outside the measured glyph bounds.
const MARGIN = 4
// Caps memory for very long names; the font shrinks to fit instead.
const MAX_CANVAS_WIDTH = 4096

interface TextToImgOptions {
	text: string
	fontFamily?: string
	fontcolor?: string
	italic?: string
	bold?: string
}

export const renderTypedSignature = ({
	text,
	fontFamily = 'Helvetica',
	fontcolor = '#000',
	italic = '',
	bold = '',
}: TextToImgOptions): string => {
	const canvas = document.createElement('canvas')
	const ctx = canvas.getContext('2d')!
	const fontFor = (size: number) => `${italic} ${bold} ${size}px ${fontFamily}`

	ctx.font = fontFor(FONT_SIZE)
	const fullWidth = ctx.measureText(text).width
	const fontSize = Math.floor(
		FONT_SIZE * Math.min(1, (MAX_CANVAS_WIDTH - MARGIN * 2) / fullWidth)
	)

	// actualBoundingBox* measures the glyphs themselves, which for script fonts reach well
	// outside the em box that textBaseline positions against.
	ctx.font = fontFor(fontSize)
	const {
		actualBoundingBoxLeft: left,
		actualBoundingBoxRight: right,
		actualBoundingBoxAscent: ascent,
		actualBoundingBoxDescent: descent,
	} = ctx.measureText(text)

	canvas.width = Math.ceil(left + right) + MARGIN * 2
	canvas.height = Math.ceil(ascent + descent) + MARGIN * 2

	// Resizing the canvas resets its state, so the font has to be set again.
	ctx.font = fontFor(fontSize)
	ctx.fillStyle = fontcolor
	ctx.fillText(text, MARGIN + left, MARGIN + ascent)

	return canvas.toDataURL()
}

// modular is typed as an opaque internal in the SDK, so describe just the bit we use.
interface FoxitModular {
	module: (name: string) => {
		getControllerClass: (
			name: string
		) =>
			| { prototype: { textToImg?: (options: TextToImgOptions) => string } }
			| undefined
	}
}

export const installHighResTypedSignatures = () => {
	const controller = (modular as unknown as FoxitModular)
		.module('inksign')
		.getControllerClass('CreateInkSignDialogController')

	// These are Foxit internals, so an SDK upgrade can rename them. Report it rather than
	// silently falling back to the clipped, blurry renderer.
	if (typeof controller?.prototype.textToImg !== 'function') {
		captureMessage(
			'Foxit typed signature renderer not found; typed signatures will use Foxit defaults',
			'warning'
		)
		return
	}

	controller.prototype.textToImg = renderTypedSignature
}
